/* NAD3 Duo — gallery, multi-variant purchase, BOGOS, synced copies, sticky checkout */
(() => {
  'use strict';
  // This file can be referenced by multiple Shopify section instances.
  if (window.__nad3DuoScriptLoaded) return;
  window.__nad3DuoScriptLoaded = true;
  const ROOT = '[data-nad3-duo]';
  const PENDING_KEY = '__nad3_duo_checkout_pending_v1';
  const RELOAD_KEY = '_nad3_duo_reload';
  const KNOWN_BOGO_KEYS = ['freegifts_code', 'freegifts_id', 'freegifts_offer', 'bogos_code'];
  let stickyListenersReady = false;
  let globalStickyRAF = 0;
  const all = (scope, selector) => Array.from(scope.querySelectorAll(selector));
  const one = (scope, selector) => scope.querySelector(selector);
  const truthy = value => value === '1';
  const numberId = value => /^\d+$/.test(String(value || '').trim()) ? Number(value) : 0;
  const sleep = delay => new Promise(resolve => setTimeout(resolve, delay));
  function getStatus(root, message) {
    const output = one(root, '[data-duo-status]');
    if (output) output.textContent = message || '';
  }
  function busy(root, active) {
    all(root, '[data-duo-checkout]').forEach(button => {
      button.disabled = active;
      button.setAttribute('aria-busy', String(active));
    });
  }
  function pricesFromPack(pack, plan) {
    if (!pack) return { otp: '', otpCompare: '', sub: '', subCompare: '' };
    const plans = plansFor(pack);
    const chosenPlan = plans.find(p => p.id === plan);
    const selectedPlanPrice = chosenPlan?.number === 2 ? pack.getAttribute('data-duo-sub-price-2') :
      chosenPlan?.number === 3 ? pack.getAttribute('data-duo-sub-price-3') : pack.dataset.duoSubPrice;
    return {
      otp: pack.dataset.duoOtpPrice || '', otpCompare: pack.dataset.duoOtpCompare || '',
      sub: selectedPlanPrice || '', subCompare: pack.dataset.duoSubCompare || ''
    };
  }
  function text(root, selector, value) {
    const el = one(root, selector);
    if (el) el.textContent = value || '';
  }
  function getPacks(root, strength) {
    return all(root, '[data-duo-pack]').filter(el => el.dataset.duoPackStrength === strength);
  }
  function getActivePack(root) {
    return getPacks(root, root.__duoState.strength).find(el => Number(el.dataset.duoPackCount) === root.__duoState.pack) || null;
  }
  function plansFor(pack) {
    if (!pack) return [];
    return [1, 2, 3].map(n => ({ id: numberId(pack.getAttribute(`data-duo-plan-${n}`)), number: n }))
      .filter(plan => plan.id > 0);
  }
  function showPricePair(root, selector, compareSelector, price, compare) {
    const priceEl = one(root, selector);
    const compareEl = one(root, compareSelector);
    function renderPrice(el, value) {
      if (!el) return;
      const display = String(value || '');
      const match = display.match(/^(.+?)([.,]\d{2})$/);
      const isPurchasePrice = el.closest('.nad3-duo__subPriceGroup, .nad3-duo__otpPriceGroup');
      if (!match || !isPurchasePrice) {
        el.textContent = display;
        return;
      }
      const whole = document.createElement('span');
      whole.className = 'nad3-duo__priceWhole';
      whole.textContent = match[1];
      const cents = document.createElement('span');
      cents.className = 'nad3-duo__priceCents';
      cents.textContent = match[2];
      el.replaceChildren(whole, cents);
    }
    renderPrice(priceEl, price || '—');
    renderPrice(compareEl, compare || '');
    if (compareEl) compareEl.hidden = !compare;
  }
  function updatePurchaseUI(root) {
    const state = root.__duoState;
    const pack = getActivePack(root);
    const purchaseAvailability = pack?.dataset.duoPurchaseAvailability || 'both';
    const subImageUrl = pack?.dataset.duoSubImage || '';
    let subImages = one(root, '.nad3-duo__subImages');
    if (!subImages && subImageUrl) {
      const subBody = one(root, '.nad3-duo__subscriptionBody');
      const priceGroup = one(root, '.nad3-duo__subPriceGroup');
      if (subBody && priceGroup) {
        subImages = document.createElement('span');
        subImages.className = 'nad3-duo__subImages';
        subImages.dataset.duoGenerated = '1';
        subBody.insertBefore(subImages, priceGroup);
      }
    }
    if (subImages) {
      all(subImages, '.nad3-duo__subImage--nad3, .nad3-duo__subImage--extra').forEach(image => {
        image.style.display = subImageUrl ? 'none' : '';
      });
      let packImage = one(subImages, '.nad3-duo__subImage--pack');
      if (subImageUrl) {
        if (!packImage) {
          packImage = document.createElement('img');
          packImage.className = 'nad3-duo__subImage nad3-duo__subImage--pack';
          packImage.loading = 'eager';
          subImages.appendChild(packImage);
        }
        if (packImage.getAttribute('src') !== subImageUrl) packImage.src = subImageUrl;
        packImage.alt = pack.dataset.duoSubImageAlt || '';
      } else {
        packImage?.remove();
        if (subImages.dataset.duoGenerated === '1') subImages.remove();
      }
    }
    const availablePlans = purchaseAvailability === 'otp' ? [] : plansFor(pack);
    const subButton = one(root, '[data-duo-purchase="sub"]');
    const otpButton = one(root, '[data-duo-purchase="otp"]');
    const frequency = one(root, '[data-duo-frequency]');
    const frequencyWrap = one(root, '[data-duo-frequency-wrap]');
    // Delivery labels for 1, 2, and 3 bottle packs.
    // Existing Theme Editor labels can still override these defaults.
    const labels = [
      root.dataset.duoPlanLabel1 || 'Every month',
      root.dataset.duoPlanLabel2 || 'Every 60 days',
      root.dataset.duoPlanLabel3 || 'Every 90 days'
    ].map(label => {
      const value = String(label).trim();
      if (/^every 2 months?$/i.test(value)) return 'Every 60 days';
      if (/^every 3 months?$/i.test(value)) return 'Every 90 days';
      return value;
    });
    const subscriptionAvailable = availablePlans.length > 0;
    subButton.disabled = !subscriptionAvailable;
    if (otpButton) otpButton.disabled = purchaseAvailability === 'sub';
    if (purchaseAvailability === 'sub') state.purchase = 'sub';
    else if (!subscriptionAvailable) state.purchase = 'otp';
    const subscriptionCard = one(root, '[data-duo-subscription-card]');
    if (subscriptionCard) {
      subscriptionCard.classList.toggle(
        'is-selected',
        state.purchase === 'sub' && availablePlans.length > 0
      );
    }
    const prevFrequency = String(state.plan || '');
    const bottleCount = Number(pack?.dataset.duoPackCount || state.pack || 1);
    const bottleIndex = Math.max(0, Math.min(labels.length - 1, bottleCount - 1));
    frequency.replaceChildren();
    availablePlans.forEach(plan => {
      // The main plan follows the selected bottle quantity.
      // Additional plans retain their independently configured slot labels.
      const labelIndex = plan.number === 1 || availablePlans.length === 1
        ? bottleIndex
        : plan.number - 1;
      const option = new Option(labels[labelIndex], String(plan.id));
      frequency.add(option);
    });
    state.plan = availablePlans.some(plan => String(plan.id) === prevFrequency) ? Number(prevFrequency) : (availablePlans[0]?.id || 0);
    frequency.value = String(state.plan || '');
    const price = pricesFromPack(pack, state.plan);
    frequencyWrap.hidden = state.purchase !== 'sub' || !availablePlans.length;
    all(root, '[data-duo-purchase]').forEach(el => {
      const active = el.dataset.duoPurchase === state.purchase;
      el.classList.toggle('is-selected', active);
      el.setAttribute('aria-pressed', String(active));
    });
    showPricePair(root, '.nad3-duo__subPriceGroup [data-duo-sub-price]', '.nad3-duo__subPriceGroup [data-duo-sub-compare]', price.sub, price.subCompare || price.otpCompare);
    showPricePair(root, '.nad3-duo__otpPriceGroup [data-duo-otp-price]', '.nad3-duo__otpPriceGroup [data-duo-otp-compare]', price.otp, price.otpCompare);
    const finalPrice = state.purchase === 'sub' ? (price.sub || price.otp) : price.otp;
    text(root, '[data-duo-sticky-price]', finalPrice);
    // Show the actual subscription discount for the selected pack, not a stale theme setting.
    const savingsBadge = one(root, '.nad3-duo__savings');
    if (savingsBadge) {
      const otpNumber = Number(String(price.otp || '').replace(/[^0-9.]/g, ''));
      const subNumber = Number(String(price.sub || '').replace(/[^0-9.]/g, ''));
      if (otpNumber > 0 && subNumber > 0 && subNumber < otpNumber) {
        const savingsPercent = Math.round((1 - subNumber / otpNumber) * 100);
        savingsBadge.textContent = `Save ${savingsPercent}%`;
      } else {
        savingsBadge.textContent = 'Subscribe & Save';
      }
    }
  }
  function updatePacksUI(root) {
    const state = root.__duoState;
    all(root, '[data-duo-strength]').forEach(el => {
      const active = el.dataset.duoStrength === state.strength;
      el.classList.toggle('is-selected', active);
      el.setAttribute('aria-pressed', String(active));
    });
    let available = getPacks(root, state.strength);
    if (!available.length) {
      state.strength = state.strength === 'nad3' ? 'extra' : 'nad3';
      available = getPacks(root, state.strength);
    }
    if (!available.some(el => Number(el.dataset.duoPackCount) === state.pack)) {
      state.pack = Number(available[0]?.dataset.duoPackCount || 1);
    }
    all(root, '[data-duo-pack]').forEach(pack => {
      const shown = pack.dataset.duoPackStrength === state.strength;
      pack.hidden = !shown;
      const chosen = shown && Number(pack.dataset.duoPackCount) === state.pack;
      pack.classList.toggle('is-selected', chosen);
      pack.setAttribute('aria-pressed', String(chosen));
      // The small pack cards display one-time prices; purchase type totals live below.
      showPricePair(pack, '[data-duo-pack-price]', '[data-duo-pack-compare]', pack.dataset.duoOtpPrice, pack.dataset.duoOtpCompare);
    });
    const mobile = one(root, '[data-duo-mobile-pack]');
    mobile.replaceChildren();
    available.sort((a,b) => Number(a.dataset.duoPackCount) - Number(b.dataset.duoPackCount)).forEach(pack => {
      const count = Number(pack.dataset.duoPackCount);
      const title = pack.querySelector('.nad3-duo__packInfo strong')?.textContent?.trim() || `${count} Bottle`;
      const subtitle = pack.querySelector('.nad3-duo__packInfo small')?.textContent?.trim() || '';
      const suffix = pack.dataset.duoOtpPrice ? ` — ${pack.dataset.duoOtpPrice}` : '';
      mobile.add(new Option(`${title} · ${subtitle}${suffix}`, String(count)));
    });
    mobile.value = String(state.pack);
    const selectedMobilePack = available.find(pack => Number(pack.dataset.duoPackCount) === state.pack);
    const previewImage = one(root, '[data-duo-mobile-pack-image]');
    if (previewImage) {
      previewImage.replaceChildren();
      const packImage = selectedMobilePack?.querySelector('.nad3-duo__packBody > img');
      if (packImage) {
        const image = packImage.cloneNode(true);
        image.loading = 'eager';
        previewImage.appendChild(image);
      }
    }
    text(root, '[data-duo-mobile-pack-title]', selectedMobilePack?.querySelector('.nad3-duo__packInfo strong')?.textContent?.trim() || '');
    text(root, '[data-duo-mobile-pack-subtitle]', selectedMobilePack?.querySelector('.nad3-duo__packInfo small')?.textContent?.trim() || '');
    updatePurchaseUI(root);
  }
  function setupPacks(root) {
    root.__duoState = {
      strength: root.dataset.duoDefaultStrength === 'extra' ? 'extra' : 'nad3',
      pack: Number(root.dataset.duoDefaultPack || 2),
      purchase: root.dataset.duoDefaultPurchase === 'otp' ? 'otp' : 'sub',
      plan: 0
    };
    all(root, '[data-duo-strength]').forEach(btn => btn.addEventListener('click', () => {
      root.__duoState.strength = btn.dataset.duoStrength;
      root.__duoState.plan = 0;
      updatePacksUI(root);
    }));
    all(root, '[data-duo-pack]').forEach(btn => btn.addEventListener('click', () => {
      root.__duoState.pack = Number(btn.dataset.duoPackCount);
      root.__duoState.plan = 0;
      updatePacksUI(root);
    }));
    one(root, '[data-duo-mobile-pack]').addEventListener('change', event => {
      root.__duoState.pack = Number(event.target.value);
      root.__duoState.plan = 0;
      updatePacksUI(root);
    });
    all(root, '[data-duo-purchase]').forEach(btn => btn.addEventListener('click', () => {
      if (btn.disabled) return;
      root.__duoState.purchase = btn.dataset.duoPurchase;
      updatePacksUI(root);
    }));
    one(root, '[data-duo-frequency]').addEventListener('change', event => {
      root.__duoState.plan = numberId(event.target.value);
      updatePacksUI(root);
    });
    updatePacksUI(root);
  }
  function setupGallery(root) {
    const gallery = one(root, '.nad3-duo__mainGallery');
    const track = gallery?.querySelector('.splide__list');
    const thumbTrack = one(root, '.nad3-duo__thumbTrack');
    if (!gallery || !track || !thumbTrack || typeof window.Splide !== 'function') return;
    const sticker = one(root, '[data-duo-sticker]');
    const prev = one(root, '[data-duo-main-prev]');
    const next = one(root, '[data-duo-main-next]');
    let main = null;
    let currentStrength = '';
    function renderGallery(strength) {
      if (strength === currentStrength) return;
      const source = one(root, `[data-duo-gallery-source="${strength}"]`);
      if (!source) return;
      const sourceSlides = source.content.querySelector('[data-duo-source-slides]');
      const sourceThumbs = source.content.querySelector('[data-duo-source-thumbs]');
      if (!sourceSlides || !sourceThumbs) return;
      if (main) main.destroy(true);
      main = null;
      root.__duoSplide = null;
      track.replaceChildren(...Array.from(sourceSlides.children, slide => slide.cloneNode(true)));
      thumbTrack.replaceChildren(...Array.from(sourceThumbs.children, thumb => thumb.cloneNode(true)));
      currentStrength = strength;
      if (sticker && truthy(sticker.dataset.duoFirstOnly)) sticker.classList.remove('is-hidden');
      if (prev) prev.disabled = true;
      if (next) next.disabled = track.children.length < 2;
      if (!track.children.length) return;
      main = new window.Splide(gallery, {type: 'slide', perPage: 1, perMove: 1, rewind: false, pagination: false, arrows: false, speed: 350, drag: true});
      root.__duoSplide = main;
      function sync(index) {
        all(thumbTrack, '[data-duo-thumb]').forEach(btn => {
          const active = Number(btn.dataset.duoThumb) === index;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-current', String(active));
          if (active) btn.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
        });
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= main.length - 1;
        if (sticker && truthy(sticker.dataset.duoFirstOnly)) sticker.classList.toggle('is-hidden', index !== 0);
      }
      main.on('mounted moved', () => sync(main.index));
      main.mount();
    }
    prev?.addEventListener('click', () => main?.go('<'));
    next?.addEventListener('click', () => main?.go('>'));
    one(root, '[data-duo-thumb-prev]')?.addEventListener('click', () => main?.go('<'));
    one(root, '[data-duo-thumb-next]')?.addEventListener('click', () => main?.go('>'));
    thumbTrack.addEventListener('click', event => {
      const thumb = event.target.closest('[data-duo-thumb]');
      if (thumb && thumbTrack.contains(thumb)) main?.go(Number(thumb.dataset.duoThumb));
    });
    gallery.addEventListener('click', event => {
      if (event.target.closest('.nad3-duo__mainImg') && main && window.innerWidth >= 900) openZoom(root, main);
    });
    all(root, '[data-duo-strength]').forEach(button => {
      button.addEventListener('click', () => renderGallery(root.__duoState.strength));
    });
    const observer = new MutationObserver(() => renderGallery(root.__duoState.strength));
    all(root, '[data-duo-strength]').forEach(button => observer.observe(button, {attributes: true, attributeFilter: ['class']}));
    renderGallery(root.__duoState.strength);
  }
  function openZoom(root, carousel) {
    const images = all(root, '.nad3-duo__mainImg').map(img => ({src: img.currentSrc || img.src, alt: img.alt || ''}));
    if (!images.length) return;
    let index = carousel.index;
    const overlay = document.createElement('div');
    overlay.className = 'nad3-duo-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Zoom product images');
    overlay.innerHTML = '\\<button type="button" class="nad3-duo-zoom__close" aria-label="Close">×\\</button>\\<button type="button" class="nad3-duo-zoom__nav nad3-duo-zoom__prev" aria-label="Previous image">❮\\</button>\\<img class="nad3-duo-zoom__image" alt="">\\<button type="button" class="nad3-duo-zoom__nav nad3-duo-zoom__next" aria-label="Next image">❯\\</button>';
    const display = overlay.querySelector('img');
    const paint = () => { display.src = images[index].src; display.alt = images[index].alt; };
    const onKey = event => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') move(1);
      if (event.key === 'ArrowLeft') move(-1);
    };
    const close = () => { overlay.remove(); document.body.classList.remove('nad3-duo-zoom-open'); document.removeEventListener('keydown', onKey); };
    const move = direction => { index = (index + direction + images.length) % images.length; paint(); };
    overlay.querySelector('.nad3-duo-zoom__close').addEventListener('click', close);
    overlay.querySelector('.nad3-duo-zoom__prev').addEventListener('click', () => move(-1));
    overlay.querySelector('.nad3-duo-zoom__next').addEventListener('click', () => move(1));
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    document.body.appendChild(overlay);
    document.body.classList.add('nad3-duo-zoom-open');
    document.addEventListener('keydown', onKey);
    paint();
    overlay.querySelector('.nad3-duo-zoom__close').focus();
  }
  function normalizeBogo(raw) {
    let value = String(raw || '').trim().replace(/&amp;/gi, '&');
    if (!value) return new URLSearchParams();
    // Accept either `freegifts_code=...`, `?freegifts_code=...`, or pasted full URL.
    if (/^https?:\/\//i.test(value)) {
      try { value = new URL(value).search.slice(1); } catch { value = ''; }
    } else if (value.includes('?')) {
      value = value.split('?').slice(1).join('?').split('#')[0];
    }
    value = value.replace(/^[?&]+/, '');
    return new URLSearchParams(value);
  }
  function bogoConfig(root) {
    // Use the selected pack and purchase option for both inherited and custom promotions.
    if (root.__duoDefaultDiscounts === undefined) root.__duoDefaultDiscounts = root.dataset.duoDiscounts || '';
    const pack = getActivePack(root);
    const purchase = root.__duoState.purchase;
    const usePackPromos = pack?.dataset.duoPromoMode === 'custom';
    const allowed = scope => !scope || scope === 'both' || scope === purchase;
    const rawDiscounts = usePackPromos ? (pack.dataset.duoPromoDiscounts || '') : root.__duoDefaultDiscounts;
    root.dataset.duoDiscounts = allowed(pack?.dataset.duoPromoDiscountPurchase) ? rawDiscounts : '';
    // Register all known promotion URL keys, even when a different pack or purchase
    // option is active. This removes obsolete BOGOS offers on a reload.
    const configuredUrls = [root.dataset.duoBogoParams || ''];
    all(root, '[data-duo-pack]').forEach(item => {
      if (item.dataset.duoPromoMode === 'custom') configuredUrls.push(item.dataset.duoPromoBogo || '');
    });
    configuredUrls.forEach(raw => {
      normalizeBogo(raw).forEach((value, key) => {
        if (key !== RELOAD_KEY && !/^(utm_|gclid$|fbclid$|msclkid$)/i.test(key) && !KNOWN_BOGO_KEYS.includes(key)) {
          KNOWN_BOGO_KEYS.push(key);
        }
      });
    });
    // Stale discount= from an earlier pack or purchase option must never leak.
    if (!KNOWN_BOGO_KEYS.includes('discount')) KNOWN_BOGO_KEYS.push('discount');
    const rawBogo = allowed(pack?.dataset.duoPromoBogoPurchase) ?
      (usePackPromos ? (pack.dataset.duoPromoBogo || '') :
      (truthy(root.dataset.duoBogoEnabled) ? root.dataset.duoBogoParams : '')) : '';
    const desired = normalizeBogo(rawBogo);
    desired.delete(RELOAD_KEY);
    for (const key of Array.from(desired.keys())) {
      if (/^(utm_|gclid$|fbclid$|msclkid$)/i.test(key)) desired.delete(key);
    }
    return desired;
  }
  function desiredBogoUrl(root) {
    const desired = bogoConfig(root);
    const url = new URL(window.location.href);
    const bogoKeys = new Set([...KNOWN_BOGO_KEYS, ...desired.keys()]);
    for (const key of [...url.searchParams.keys()]) {
      if (bogoKeys.has(key)) url.searchParams.delete(key);
    }
    desired.forEach((value,key) => url.searchParams.append(key,value));
    url.searchParams.delete(RELOAD_KEY);
    return url;
  }
  function parametersMatch(urlA, urlB, root) {
    const desired = bogoConfig(root);
    const keys = new Set([...KNOWN_BOGO_KEYS, ...desired.keys()]);
    return [...keys].every(key => JSON.stringify(urlA.searchParams.getAll(key)) === JSON.stringify(urlB.searchParams.getAll(key)));
  }
  function savePending(root) {
    const state = { section: root.dataset.duoSectionId, strength: root.__duoState.strength, pack: root.__duoState.pack,
      purchase: root.__duoState.purchase, plan: root.__duoState.plan, when: Date.now() };
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(state)); } catch {}
  }
  function getPending() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      if (saved && Date.now() - saved.when < 45000) return saved;
    } catch {}
    return null;
  }
  function clearPending() { try { sessionStorage.removeItem(PENDING_KEY); } catch {} }
  function prepareBogoReload(root) {
    const target = desiredBogoUrl(root);
    if (parametersMatch(new URL(window.location.href), target, root)) return false;
    savePending(root);
    target.searchParams.set(RELOAD_KEY, root.dataset.duoSectionId);
    window.location.replace(target.toString());
    return true;
  }
  function stripReloadMarker() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_KEY)) return;
    url.searchParams.delete(RELOAD_KEY);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  }
  function publicParameters(checkoutUrl, root) {
    const source = new URL(window.location.href);
    for (const [key, value] of source.searchParams.entries()) {
      if (key !== RELOAD_KEY && key !== 'discount' && !checkoutUrl.searchParams.has(key)) checkoutUrl.searchParams.append(key, value);
    }
    // The canonical BOGOS values win over any stale values on the checkout URL.
    bogoConfig(root).forEach((value,key) => checkoutUrl.searchParams.set(key, value));
  }
  async function requestJson(url, payload) {
    const response = await fetch(url, {
      method: payload === undefined ? 'GET' : 'POST',
      credentials: 'same-origin', cache: 'no-store',
      headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      ...(payload === undefined ? {} : {body: JSON.stringify(payload)})
    });
    let result;
    try { result = await response.json(); } catch { result = {}; }
    if (!response.ok) throw new Error(result.description || result.message || `Cart request failed (${response.status})`);
    return result;
  }
  function configuredGifts(root) {
    try {
      const gifts = JSON.parse(one(root,'[data-duo-gifts-json]')?.textContent || '[]');
      const selectedPack = `${root.__duoState.strength}:${root.__duoState.pack}`;
      const cartGiftCards = all(root, '.nad3-duo__gift[data-duo-gift-auto-add="1"]');
      return gifts.filter((g, index) => {
        const unavailableForPurchase = String(cartGiftCards[index]?.dataset.duoGiftBlurPurchase || '').trim().split(/\s+/).includes(root.__duoState.purchase);
        return ['both', root.__duoState.strength].includes(g.strength || 'nad3') && !String(g.blur || '').trim().split(/\s+/).includes(selectedPack) && !unavailableForPurchase;
      }).map(g => ({id: numberId(g.id), quantity: Math.max(1, Number(g.quantity) || 1)})).filter(g => g.id > 0);
    } catch { return []; }
  }
  async function waitForAutomaticGifts(minimumCount, root) {
    if (!bogoConfig(root).size) return;
    // Give BOGOS a chance to add an automatic gift; do not block checkout indefinitely.
    for (let i = 0; i < 8; i++) {
      await sleep(300);
      try {
        const cart = await requestJson('/cart.js');
        if (cart.item_count > minimumCount) return;
      } catch { return; }
    }
  }
  function parseDiscountCodes(raw) {
    return String(raw || '').split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
  }
  async function checkout(root) {
    if (root.__duoSubmitting) return;
    root.__duoSubmitting = true;
    getStatus(root,'');
    busy(root,true);
    try {
      const state = root.__duoState;
      const pack = getActivePack(root);
      if (!pack) throw new Error('Configure the quantity pack blocks in Theme Editor.');
      const variant = numberId(pack.dataset.duoVariantId);
      const quantity = Math.max(1, Number(pack.dataset.duoLineQty) || 1);
      if (!variant) throw new Error('This pack needs a valid Shopify variant ID in Theme Editor.');
      if (state.purchase === 'sub' && !numberId(state.plan)) throw new Error('This subscription pack needs a valid selling plan ID.');
      // Validate selections BEFORE a BOGOS navigation/reload.
      const resumedAfterReload = !!root.__duoReloadResumed;
      root.__duoReloadResumed = false;
      if (!resumedAfterReload && prepareBogoReload(root)) return;
      const mainItem = {id: variant, quantity};
      if (state.purchase === 'sub') mainItem.selling_plan = numberId(state.plan);
      const giftsPolicy = root.dataset.duoFreebiePolicy || 'sub';
      const gifts = giftsPolicy === 'both' || (giftsPolicy === 'sub' && state.purchase === 'sub') ? configuredGifts(root) : [];
      if (truthy(root.dataset.duoClearCart)) await requestJson('/cart/clear.js', {});
      const toAdd = [mainItem, ...gifts];
      // Main product is mandatory. A failed gift add should not cause a partial checkout.
      await requestJson('/cart/add.js', {items: toAdd});
      let expectedCount = toAdd.reduce((total, item) => total + item.quantity, 0);
      if (!truthy(root.dataset.duoClearCart)) {
        // When not clearing the cart, derive the count after the add instead.
        try { expectedCount = (await requestJson('/cart.js')).item_count; } catch {}
      }
      const bogoAttrs = Object.fromEntries(KNOWN_BOGO_KEYS.map(key => [key, '']));
      bogoConfig(root).forEach((value,key) => { bogoAttrs[key] = value; });
      // Always replace known stale BOGOS cart attributes, including when disabled.
      await requestJson('/cart/update.js', {attributes: bogoAttrs});
      await waitForAutomaticGifts(expectedCount, root);
      const discount = parseDiscountCodes(root.dataset.duoDiscounts);
      const checkoutUrl = new URL(root.dataset.duoRedirect || '/checkout', window.location.origin);
      publicParameters(checkoutUrl, root);
      if (discount.length) checkoutUrl.searchParams.set('discount', discount.join(','));
      // Direct-to-checkout flow, as in the HPN reference buy box.
      window.location.assign(checkoutUrl.toString());
    } catch (error) {
      getStatus(root, error.message || 'Unable to complete the cart request. Please try again.');
      root.__duoSubmitting = false;
      busy(root,false);
      clearPending();
    }
  }
  function restorePendingCheckout(root) {
    const pending = getPending();
    if (!pending || pending.section !== root.dataset.duoSectionId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get(RELOAD_KEY) !== pending.section) { clearPending(); return; }
    root.__duoState.strength = pending.strength;
    root.__duoState.pack = pending.pack;
    root.__duoState.purchase = pending.purchase;
    root.__duoState.plan = pending.plan;
    updatePacksUI(root);
    stripReloadMarker();
    clearPending();
    root.__duoReloadResumed = true;
    window.requestAnimationFrame(() => {
      const cta = one(root, '.nad3-duo__cta');
      cta?.scrollIntoView({ behavior: 'instant', block: 'center' });
      checkout(root);
    });
  }
  function visibleStickyUpdate() {
    const eligible = all(document, ROOT).filter(root => {
      const bar = one(root, '[data-duo-sticky]');
      if (!bar || !truthy(root.dataset.duoStickyEnabled) || root.dataset.duoSyncRole === 'copy') return false;
      const main = one(root, '.nad3-duo__cta');
      if (!main) return false;
      const rect = main.getBoundingClientRect();
      if (truthy(root.dataset.duoStickyAlways)) return true;
      if (truthy(root.dataset.duoStickyBelow)) return rect.bottom < 0;
      return rect.bottom < 0 || rect.top > window.innerHeight;
    });
    // Only a single sticky bar can own the bottom of the viewport at a time.
    const winner = eligible.length ? eligible[eligible.length - 1] : null;
    all(document, ROOT).forEach(root => {
      const bar = one(root, '[data-duo-sticky]');
      if (!bar) return;
      bar.classList.toggle('is-visible', root === winner);
      bar.setAttribute('aria-hidden', String(root !== winner));
    });
  }
  function scheduleStickyUpdate() {
    if (globalStickyRAF) return;
    globalStickyRAF = requestAnimationFrame(() => { globalStickyRAF = 0; visibleStickyUpdate(); });
  }
  function setupStickyListeners() {
    if (stickyListenersReady) return;
    stickyListenersReady = true;
    window.addEventListener('scroll', scheduleStickyUpdate, {passive:true});
    window.addEventListener('resize', scheduleStickyUpdate, {passive:true});
  }
  function syncCopies() {
    const roots = all(document, ROOT);
    const masters = new Map();
    roots.filter(root => root.dataset.duoSyncRole === 'source').forEach(root => {
      const group = root.dataset.duoSyncGroup || '';
      if (group && !masters.has(group)) masters.set(group, root);
    });
    roots.filter(root => root.dataset.duoSyncRole === 'copy').forEach(copy => {
      const group = copy.dataset.duoSyncGroup || '';
      const master = masters.get(group);
      if (!master) { copy.style.visibility = ''; return; }
      // Keep the COPY'S section ID and anchor so scrolling/editor selection remain unique.
      const localId = copy.id;
      const localSectionId = copy.dataset.duoSectionId;
      if (copy.__duoSplide) { try { copy.__duoSplide.destroy(true); } catch {} }
      copy.__duoGiftPopupsCleanup?.();
      copy.innerHTML = master.innerHTML;
      for (const name of ['duoBogoEnabled','duoBogoParams','duoClearCart','duoDiscounts','duoFreebiePolicy',
        'duoCurrency','duoRedirect','duoPlanLabel1','duoPlanLabel2','duoPlanLabel3','duoDefaultStrength','duoDefaultPack','duoDefaultPurchase','duoStickyEnabled']) {
        copy.dataset[name] = master.dataset[name];
      }
      copy.id = localId;
      copy.dataset.duoSectionId = localSectionId;
      copy.dataset.duoStickyEnabled = '0';
      one(copy,'[data-duo-sticky]')?.remove();
      const originalFrequency = one(copy,'[data-duo-frequency]');
      if (originalFrequency) {
        const id = `nad3-duo-frequency-${localSectionId}`;
        originalFrequency.id = id;
        const label = one(copy,'.nad3-duo__frequencyWrap label');
        if (label) label.setAttribute('for',id);
      }
      all(copy,'[data-shopify-editor-block], [data-shopify-editor-section]').forEach(el => {
        el.removeAttribute('data-shopify-editor-block');
        el.removeAttribute('data-shopify-editor-section');
      });
      const computed = getComputedStyle(master);
      ['--nd-bg-start','--nd-bg-end','--nd-highlight','--nd-border','--nd-text','--nd-card'].forEach(name => {
        copy.style.setProperty(name, computed.getPropertyValue(name));
      });
      copy.__duoInitialized = false;
      copy.style.visibility = '';
    });
  }
  // Optional Gift / Shipping card popups, independently configured on each block.
  function setupGiftPopups(root) {
    if (root.__duoGiftPopupsCleanup) root.__duoGiftPopupsCleanup();
    let focusedBeforeOpen = null;
    let activePopup = null;

    function releaseScrollLock() {
      if (!document.querySelector('.nad3-duo-gift__popup.is-open')) {
        document.documentElement.classList.remove('nad3-duo-gift-popup-open');
        document.body.classList.remove('nad3-duo-gift-popup-open');
      }
    }

    function closePopup(restoreFocus = true) {
      if (!activePopup) return;
      activePopup.classList.remove('is-open');
      activePopup.setAttribute('aria-hidden', 'true');
      activePopup = null;
      releaseScrollLock();
      if (restoreFocus && focusedBeforeOpen?.isConnected) focusedBeforeOpen.focus({ preventScroll: true });
      focusedBeforeOpen = null;
    }

    function isGiftAvailable(gift) {
      const state = root.__duoState;
      if (!state || !gift) return false;
      const strength = gift.dataset.duoGiftStrength || 'nad3';
      if (strength !== 'both' && strength !== state.strength) return false;
      return !(gift.dataset.duoGiftBlur || '').split(/\s+/).includes(`${state.strength}:${state.pack}`);
    }

    function openPopup(trigger) {
      const gift = trigger.closest('.nad3-duo__gift');
      if (!isGiftAvailable(gift)) return;
      const popupId = trigger.getAttribute('data-duo-gift-popup-open');
      const popup = all(root, '[data-duo-gift-modal]').find(el => el.dataset.duoGiftModal === popupId);
      if (!popup) return;
      closePopup(false);
      // This remains scoped to its Duo instance, including synchronized copies.
      focusedBeforeOpen = trigger;
      activePopup = popup;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('nad3-duo-gift-popup-open');
      document.body.classList.add('nad3-duo-gift-popup-open');
      (one(popup, '.nad3-duo-gift__popupClose') || one(popup, '[role="dialog"]'))?.focus({ preventScroll: true });
    }

    function scrollToPopupTarget(rawId) {
      const targetId = String(rawId || '').trim().replace(/^#/, '');
      const target = (targetId && document.getElementById(targetId)) || one(root, '.nad3-duo__purchaseChoices') || root;
      const y = target.getBoundingClientRect().top + window.pageYOffset - 96;
      window.scrollTo({ top: Math.max(0, y), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    const listeners = new AbortController();
    root.__duoGiftPopupsCleanup = () => { closePopup(false); listeners.abort(); };
    root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-duo-gift-popup-open]');
      if (trigger && root.contains(trigger)) {
        event.preventDefault();
        openPopup(trigger);
        return;
      }
      const close = event.target.closest('[data-duo-gift-popup-close]');
      if (close && activePopup?.contains(close)) {
        event.preventDefault();
        closePopup();
        return;
      }
      const cta = event.target.closest('[data-duo-gift-popup-cta]');
      if (cta && activePopup?.contains(cta)) {
        event.preventDefault();
        const target = cta.dataset.duoGiftPopupTarget;
        closePopup(false);
        scrollToPopupTarget(target);
        return;
      }
      if (activePopup && event.target.closest('[data-duo-strength], [data-duo-pack], [data-duo-mobile-pack]')) closePopup(false);
    }, { signal: listeners.signal });

    root.addEventListener('change', event => {
      if (activePopup && event.target.matches('[data-duo-mobile-pack]')) closePopup(false);
    }, { signal: listeners.signal });

    document.addEventListener('keydown', event => {
      if (!activePopup) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = all(activePopup, 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex="0"]')
        .filter(el => el.getClientRects().length);
      if (!focusable.length) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, { signal: listeners.signal });

    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(root)) root.__duoGiftPopupsCleanup?.();
    }, { signal: listeners.signal });
  }

  function initRoot(root) {
    if (root.__duoInitialized) return;
    root.__duoInitialized = true;
    setupPacks(root);
    setupGallery(root);
    setupGiftPopups(root);
    all(root, '[data-duo-checkout]').forEach(button => button.addEventListener('click', () => checkout(root)));
    setupStickyListeners();
    restorePendingCheckout(root);
    scheduleStickyUpdate();
  }
  function init() {
    syncCopies();
    all(document, ROOT).forEach(initRoot);
    scheduleStickyUpdate();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
